import React, { useEffect, useState } from 'react';
import IconItem from './icon-item';
import ImageGallery from './image-gallery';
import { BREAKPOINTS } from '../../helpers/breakpoints';
import { useDebounce } from '../../helpers/debounce';
import { createDataLayerString, makeSuperScript } from '../../helpers/helpers';
import {
  ExtendedWindowType,
  MediaType,
  ProductMastheadResponseType,
  VariantOptionType
} from '../../helpers/global-types';

type ProductMastheadProps = {
  nextLabel: string;
  prevLabel: string;
  selectDefault: string;
  defaultImage: string;
};

const ProductMasthead = ({
  nextLabel,
  prevLabel,
  selectDefault,
  defaultImage
}: ProductMastheadProps) => {
  const [data, setData] = useState<ProductMastheadResponseType>();
  const [error, setError] = useState(false);
  const [isTablet, setIsTablet] = useState(
    window.matchMedia(`(max-width: ${BREAKPOINTS.tablet - 2}px)`).matches
  );
  const [displayImages, setDisplayImages] = useState<MediaType[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<
    VariantOptionType[]
  >([]);
  const [secondLevelOptions, setSecondLevelOptions] = useState<
    VariantOptionType[]
  >([]);
  const [manualUpdate, setManualUpdate] = useState(false);
  const [personalizeButtons, setPersonalizeButtons] = useState('');
  const selectOption = {
    id: 'default',
    label: selectDefault,
    products: []
  };

  const getVariantAvailable = (index: number, option: VariantOptionType) => {
    if (index === 1 && selectedAttributes[1]?.id === 'default') {
      return true;
    }

    const sameProducts = selectedAttributes[index]?.products.filter(value =>
      option.products.includes(value)
    );

    if (sameProducts?.length > 0) {
      return true;
    }
    return false;
  };

  const handleDefaultSelectedAttributes = () => {
    if (data?.defaultVariation !== '') {
      const selectedVariation = data?.pid_attribute_option_index.find(
        item => item.magento_product_id === data?.defaultVariation
      );
      if (selectedVariation) {
        const selectedOptions: VariantOptionType[] = [];
        data?.variants.forEach(variant => {
          const correspondingVariant =
            selectedVariation.attribute_option_items.find(
              attributeOption => attributeOption.attribute_id === variant.id
            );
          if (correspondingVariant) {
            const selectedOption = variant.options.find(
              opt => opt.id === correspondingVariant.option_id
            );
            if (selectedOption) {
              selectedOptions.push(selectedOption);
            }
          }
        });

        if (selectedOptions.length === 0) {
          data?.variants.forEach(variant => {
            selectedOptions.push(variant.options[0]);
          });
        }

        setSelectedAttributes(selectedOptions);
      }
    } else {
      const selectedOptions: VariantOptionType[] = [];
      data?.variants.forEach(variant => {
        selectedOptions.push(variant.options[0]);
      });

      setSelectedAttributes(selectedOptions);
    }
  };

  const handleChangeSelected = () => {
    if (data?.variants && data?.variants.length > 1) {
      getSecondLevelOptions(data?.variants[1].options as VariantOptionType[]);
      data?.variants[1].options.find(option => {
        if (secondLevelOptions.length > 6) {
          setSelectedAttributes(prev => {
            prev.pop();
            prev.push(selectOption);
            return [...prev];
          });
        } else if (getVariantAvailable(0, option)) {
          setSelectedAttributes(prev => {
            prev.pop();
            prev.push(option);
            return [...prev];
          });
          return true;
        }
        return false;
      });
    }
  };

  const handleAdditionalStates = (index: number, option: VariantOptionType) => {
    let classes = '';
    if (selectedAttributes[index]?.id === option.id) {
      classes = 'product-masthead__option--active';
    }

    if (index === 0 && data?.variants.length && data?.variants.length > 1) {
      if (!getVariantAvailable(1, option)) {
        classes = 'product-masthead__option--not-available';
      }
    }

    return classes;
  };

  const handleDisplayMedia = () => {
    const mapping = data?.pid_attribute_option_index;
    const id = mapping?.find(item => {
      return data?.variants.every((variant, index) => {
        const selProduct = item.attribute_option_items.find(
          option =>
            option.attribute_id === variant.id &&
            selectedAttributes[index].id === option.option_id
        );
        return selProduct;
      });
    });

    const mediaImage = data?.mediaVariants.find(mediaVariant => {
      if (mediaVariant.magento_product_id === id?.magento_product_id) {
        if (mediaVariant.media.length > 0) {
          return mediaVariant;
        }
      }
      return false;
    });
    if (!mediaImage && data?.productDescription?.defaultImages) {
      if (data?.productDescription?.defaultImages.length > 0) {
        setDisplayImages(data?.productDescription.defaultImages);
      } else {
        setDisplayImages(JSON.parse(defaultImage));
      }
    } else if (!mediaImage) {
      setDisplayImages(JSON.parse(defaultImage));
    } else {
      setDisplayImages(mediaImage.media);
    }

    if (data?.variants && data?.variants.length > 1)
      getSecondLevelOptions(data?.variants[1].options as VariantOptionType[]);
  };

  const getSecondLevelOptions = (options: VariantOptionType[]) => {
    const displaySecondLvlOptions: VariantOptionType[] = [];

    if (selectedAttributes[0]) {
      options.forEach(option => {
        if (getVariantAvailable(0, option)) {
          displaySecondLvlOptions.push(option);
        }
      });
    }
    if (displaySecondLvlOptions.length > 6) {
      displaySecondLvlOptions.unshift(selectOption);
    }
    setSecondLevelOptions(displaySecondLvlOptions);
  };

  useEffect(() => {
    if (
      !secondLevelOptions.find(
        option => option.id === selectedAttributes[1]?.id
      )
    ) {
      handleChangeSelected();
    }
  }, [secondLevelOptions]);

  const handleResize = useDebounce(() => {
    const tabletMatch = window.matchMedia(
      `(max-width: ${BREAKPOINTS.tablet - 2}px)`
    ).matches;
    if (isTablet !== tabletMatch) {
      setIsTablet(prev => !prev);
    }
  }, 100);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    let observer: MutationObserver;

    const targetNode = document.querySelector('.personalize-buttons');

    if (targetNode) {
      const targetInnerHtml = targetNode.innerHTML;

      setPersonalizeButtons(targetInnerHtml);

      const config = { attributes: true, childList: true, subtree: true };

      const callback = (mutationList: MutationRecord[]) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            setPersonalizeButtons(targetNode.innerHTML);
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(targetNode, config);
    }

    const newData = (window as ExtendedWindowType).mastheadData;

    if (newData) {
      setData((window as ExtendedWindowType).mastheadData);
    } else {
      setError(true);
    }

    return () => {
      window.removeEventListener('resize', handleResize);

      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (data) {
      handleDefaultSelectedAttributes();
    }
  }, [data]);

  useEffect(() => {
    if (!manualUpdate) {
      handleDisplayMedia();
    } else {
      setManualUpdate(false);
    }
  }, [selectedAttributes]);

  const renderFirstLevelOptions = (
    options: VariantOptionType[],
    heading: string
  ) => {
    if (options.length > 6) {
      return (
        <div className="product-masthead__select-wrapper">
          <select
            className="product-masthead__select"
            onChange={e => {
              (window as ExtendedWindowType).dataLayer.push({
                event: 'product_click',
                event_category: 'product',
                event_action: 'variant selection',
                event_label: heading,
                event_attributes: {
                  product_name: data?.productDescription?.heading,
                  product_category: data?.productDescription?.icon?.label,
                  average_rating: null
                }
              });
              const selOption = options.find(
                option => option.id === e.currentTarget.value
              );
              if (selOption) {
                setSelectedAttributes(prev => {
                  prev.shift();
                  prev.unshift(selOption);
                  return [...prev];
                });
              }
            }}
            value={selectedAttributes[0]?.id}
          >
            {options.map(option => (
              <option
                className="product-masthead__select-option"
                key={option.id}
                value={option.id}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    } else {
      return (
        <>
          {options.map(option => (
            <button
              className={`product-masthead__option ${handleAdditionalStates(
                0,
                option
              )}`}
              onClick={() => {
                (window as ExtendedWindowType).dataLayer.push({
                  event: 'product_click',
                  event_category: 'product',
                  event_action: 'variant selection',
                  event_label: heading,
                  event_attributes: {
                    product_name: data?.productDescription.heading,
                    product_category: data?.productDescription?.icon?.label,
                    average_rating: null
                  }
                });
                setSelectedAttributes(prev => {
                  prev.shift();
                  prev.unshift(option);
                  return [...prev];
                });
              }}
              key={option.id}
            >
              {option.label}
            </button>
          ))}
        </>
      );
    }
  };
  const renderSecondLevelOptions = (heading: string) => {
    if (secondLevelOptions.length > 6) {
      return (
        <div className="product-masthead__select-wrapper">
          <select
            className="product-masthead__select"
            onChange={e => {
              (window as ExtendedWindowType).dataLayer.push({
                event: 'product_click',
                event_category: 'product',
                event_action: 'variant selection',
                event_label: heading,
                event_attributes: {
                  product_name: data?.productDescription.heading,
                  product_category: data?.productDescription?.icon?.label,
                  average_rating: null
                }
              });
              const selOption = secondLevelOptions.find(
                option => option.id === e.currentTarget.value
              );
              if (selOption) {
                setSelectedAttributes(prev => {
                  prev.pop();
                  prev.push(selOption);
                  return [...prev];
                });
              }
            }}
            value={selectedAttributes[1]?.id}
          >
            {secondLevelOptions.map(option => (
              <option
                className="product-masthead__select-option"
                value={option.id}
                key={option.id}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    } else {
      return secondLevelOptions.map(option => (
        <button
          className={`product-masthead__option ${handleAdditionalStates(
            1,
            option
          )}`}
          onClick={() => {
            (window as ExtendedWindowType).dataLayer.push({
              event: 'product_click',
              event_category: 'product',
              event_action: 'variant selection',
              event_label: heading,
              event_attributes: {
                product_name: data?.productDescription?.heading,
                product_category: data?.productDescription?.icon?.label,
                average_rating: null
              }
            });
            setSelectedAttributes(prev => {
              prev.pop();
              prev.push(option);
              return [...prev];
            });
          }}
          key={option.id}
        >
          {option.label}
        </button>
      ));
    }
  };

  if (error) {
    return <p>Error on loading</p>;
  }

  return (
    <div className="product-masthead__wrapper row">
      {!isTablet && (
        <div className="product-masthead__swiper">
          <ImageGallery
            images={displayImages}
            nextLabel={nextLabel}
            prevLabel={prevLabel}
            productHeading={data?.productDescription?.heading || ''}
            category={data?.productDescription?.icon?.label || ''}
          />
        </div>
      )}
      <div className="product-masthead__content">
        <div className="product-masthead__top-content">
          {data?.productDescription?.icon && (
            <IconItem
              src={data?.productDescription?.icon.src}
              label={data?.productDescription?.icon?.label}
            />
          )}
          <div className="product-masthead__info">
            <h1
              className="product-masthead__heading rtf--inline"
              dangerouslySetInnerHTML={{
                __html: makeSuperScript(
                  data?.productDescription?.heading as string
                ) as string
              }}
            />
            <div
              className="product-masthead__description"
              dangerouslySetInnerHTML={{
                __html: makeSuperScript(
                  data?.productDescription.description as string
                ) as string
              }}
            />
          </div>
          {personalizeButtons && (
            <div
              className="product-masthead__button-row"
              dangerouslySetInnerHTML={{ __html: personalizeButtons }}
            ></div>
          )}
        </div>
        {isTablet ? (
          <div className="product-masthead__swiper">
            <ImageGallery
              images={displayImages}
              nextLabel={nextLabel}
              prevLabel={prevLabel}
              productHeading={data?.productDescription?.heading || ''}
              category={data?.productDescription?.icon?.label || ''}
            />
          </div>
        ) : null}

        {data?.variants?.map((variant, index) => (
          <div className="product-masthead__variant" key={variant.id}>
            <p className="product-masthead__variant-heading">{variant.label}</p>
            <div className="product-masthead__variant-wrapper">
              {index === 0 &&
                renderFirstLevelOptions(variant.options, variant.label)}
              {index === 1 && <>{renderSecondLevelOptions(variant.label)}</>}
            </div>
          </div>
        ))}

        {data?.productDescription?.documentQuickLinks?.length &&
        data?.productDescription?.documentQuickLinks?.length > 0 ? (
          <div>
            <p className="product-masthead__variant-heading">
              {data?.productDescription.documentQuickLinksLabel}
            </p>
            <div className="product-masthead__button-row">
              {data?.productDescription.documentQuickLinks?.map(link => {
                if (link.type === 'link') {
                  return (
                    <a
                      key={link.label}
                      href={link.url}
                      className="link link--arrow"
                      target="_blank"
                    >
                      <span>{link.label}</span>
                    </a>
                  );
                } else if (link.type === 'download') {
                  return (
                    <a
                      target="_blank"
                      key={link.label}
                      href={link.url}
                      className="link link--download"
                      download={true}
                      data-layer={createDataLayerString([
                        {
                          event_category: 'document interactions',
                          event_attributes: {
                            custom_file_name: link.label
                          },
                          event: 'file_download_click',
                          event_action: 'file download click'
                        }
                      ])}
                    >
                      <span>{link.label}</span>
                    </a>
                  );
                } else if (link.type === 'external') {
                  return (
                    <a
                      key={link.label}
                      href={link.url}
                      className="link link--external"
                      target="_blank"
                      data-layer={createDataLayerString([
                        {
                          event_category: 'outbound link',
                          event: 'outbound_link_click'
                        }
                      ])}
                    >
                      <span>{link.label}</span>
                    </a>
                  );
                }
                return null;
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
export default ProductMasthead;
